import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import CardImage from '../components/CardImage';
import { banStatus, BAN_BADGE } from '../banlist';
import CardFilterBar, { type FilterValue } from '../components/CardFilterBar';
import { cardsApi } from '../api/cardsApi';
import { cardImageUrl } from '../config';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import type { CardsResponse, Filters, CardSort } from '../../../shared/types';

const PAGE_SIZE = 30;
const SORTS: CardSort[] = ['name', 'popularity', 'atk', 'def', 'level'];

export default function CardSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the source of truth for shareable/refresh-safe state.
  const urlSearch = searchParams.get('search') ?? '';
  const sort = (searchParams.get('sort') as CardSort) || 'name';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);

  // Comma-separated multi-value filters. Keep the raw strings for effect deps,
  // and derived arrays for rendering.
  const categoriesStr = searchParams.get('categories') ?? '';
  const attributesStr = searchParams.get('attributes') ?? '';
  const monsterTypesStr = searchParams.get('monsterTypes') ?? '';
  const monsterCategoriesStr = searchParams.get('monsterCategories') ?? '';
  const spellTypesStr = searchParams.get('spellTypes') ?? '';
  const trapTypesStr = searchParams.get('trapTypes') ?? '';
  const skillCharactersStr = searchParams.get('skillCharacters') ?? '';
  const setsStr = searchParams.get('sets') ?? '';
  const toList = (s: string) => (s ? s.split(',').filter(Boolean) : []);
  const categories = toList(categoriesStr);
  const attributes = toList(attributesStr);
  const monsterTypes = toList(monsterTypesStr);
  const monsterCategories = toList(monsterCategoriesStr);
  const spellTypes = toList(spellTypesStr);
  const trapTypes = toList(trapTypesStr);
  const skillCharacters = toList(skillCharactersStr);
  const sets = toList(setsStr);

  // Local text state so typing is snappy; debounced value drives the URL.
  const [searchInput, setSearchInput] = useState(urlSearch);
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  const [filters, setFilters] = useState<Filters | null>(null);
  const [data, setData] = useState<CardsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setSearchInput(urlSearch); }, [urlSearch]);

  // Push the debounced search term into the URL (resetting to page 1).
  useEffect(() => {
    if (debouncedSearch === urlSearch) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (debouncedSearch) next.set('search', debouncedSearch);
      else next.delete('search');
      next.delete('page');
      return next;
    }, { replace: true });
  }, [debouncedSearch, urlSearch, setSearchParams]);

  // Load filter option lists once.
  useEffect(() => {
    cardsApi.filters().then(setFilters).catch(() => setFilters(null));
  }, []);

  // Load cards whenever the effective query changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    cardsApi
      .search({
        search: urlSearch || undefined,
        categories: toList(categoriesStr),
        attributes: toList(attributesStr),
        monsterTypes: toList(monsterTypesStr),
        monsterCategories: toList(monsterCategoriesStr),
        spellTypes: toList(spellTypesStr),
        trapTypes: toList(trapTypesStr),
        skillCharacters: toList(skillCharactersStr),
        sets: toList(setsStr),
        page,
        pageSize: PAGE_SIZE,
        sort,
      })
      .then(res => { if (!cancelled) setData(res); })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load cards'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearch, categoriesStr, attributesStr, monsterTypesStr, monsterCategoriesStr,
      spellTypesStr, trapTypesStr, skillCharactersStr, setsStr, sort, page]);

  function setList(key: string, values: string[]) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (values.length) next.set(key, values.join(','));
      else next.delete(key);
      next.delete('page'); // any filter change resets pagination
      return next;
    });
  }

  function updateParam(key: string, value: string) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete('page');
      return next;
    });
  }

  function clearAll() {
    setSearchParams(prev => {
      const next = new URLSearchParams();
      const s = prev.get('sort');
      if (s) next.set('sort', s);
      return next;
    });
  }

  function goToPage(next: number) {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (next <= 1) p.delete('page');
      else p.set('page', String(next));
      return p;
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const filterValue: FilterValue = {
    categories, attributes, monsterTypes, monsterCategories,
    spellTypes, trapTypes, skillCharacters, sets,
  };

  const totalPages = data?.totalPages ?? 0;
  const total = data?.total ?? 0;
  const cards = useMemo(() => data?.cards ?? [], [data]);

  return (
    <div>
      <SiteHeader />
      <main className="page page--wide">
        <h1>Speed Duel Card Search</h1>

        <div className="toolbar" style={{ marginBottom: 12 }}>
          <input
            type="search"
            placeholder="Search name or effect…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{ flex: '1 1 240px', minWidth: 200 }}
            aria-label="Search cards"
          />
          <select value={sort} onChange={e => updateParam('sort', e.target.value)} aria-label="Sort by">
            {SORTS.map(s => <option key={s} value={s}>Sort: {s}</option>)}
          </select>
          <CardFilterBar
            filters={filters}
            value={filterValue}
            onChange={(k, v) => setList(k, v)}
            onClear={clearAll}
          />
        </div>

        {error && <p className="error">{error}</p>}

        {!error && (
          <p className="muted" style={{ margin: '12px 0' }}>
            {loading && !data ? 'Loading…' : `${total} card${total === 1 ? '' : 's'} found`}
          </p>
        )}

        <div className="card-grid" style={{ opacity: loading ? 0.6 : 1 }}>
          {cards.map(card => {
            const ban = banStatus(card.name);
            return (
              <Link key={card.name} to={`/card/${encodeURIComponent(card.name)}`} className="card-grid-item">
                {ban && <span className={`ban-badge ${BAN_BADGE[ban].cls}`} title={BAN_BADGE[ban].title}>{BAN_BADGE[ban].symbol}</span>}
                <CardImage src={cardImageUrl(card.primaryImage)} alt={card.nameDisplay ?? card.name} />
                <span className="card-grid-name">{card.nameDisplay ?? card.name}</span>
              </Link>
            );
          })}
        </div>

        {!loading && !error && cards.length === 0 && (
          <p className="muted" style={{ textAlign: 'center', marginTop: 40 }}>
            No cards match your search.
          </p>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button type="button" onClick={() => goToPage(page - 1)} disabled={page <= 1}>← Prev</button>
            <span className="muted">Page {page} of {totalPages}</span>
            <button type="button" onClick={() => goToPage(page + 1)} disabled={page >= totalPages}>Next →</button>
          </div>
        )}
      </main>
    </div>
  );
}
